import ArcNumber from '@arcxgame/contracts/dist/src/utils/ArcNumber'
import { useErc20Details } from '@arcxmoney/arcx-react'
import { BigNumber, ContractTransaction } from 'ethers'
import React, { useContext, useState } from 'react'
import Loader from 'react-loader-spinner'
import { ArcContext } from 'src/contexts/ArcContext'
import { getReadableNumber } from 'src/helpers/getReadableNumber'
import { useSaleEligibility } from 'src/hooks'
import { useErc20Balance } from 'src/hooks/useErc20Balance'
import { useLocation } from 'src/hooks/useLocation'
import { approve } from 'src/utils/approve'
import { ArcButton } from '../ArcButton'
import { InfoBox } from '../InfoBox'
import { Input } from '../Input'
import { Title } from '../Title'
import './PhaseSale.scss'

export function PhaseSale(props: { phase: number; arcPrice: string; terms: string }) {
  const { arc, isLoading, setIsLoading } = useContext(ArcContext)
  const [isTermsAccepted, setIsTermsAccepted] = useState(false)
  const eligibility = useSaleEligibility(props.phase)
  const { participant, currency, whitelistContract, totalRaised, hardcap, saleStarted } =
    eligibility
  const erc20Details = useErc20Details(currency, arc?.signer)
  const usdcBalance = useErc20Balance(currency, arc?.signer)
  const [inputAmount, setInputAmount] = useState(BigNumber.from(0))

  const { location, error: locationError, loading: locationLoading } = useLocation()

  const onPurchaseClicked = async () => {
    if (!whitelistContract || !arc?.signer) {
      return
    }

    setIsLoading(true)

    try {
      await approve(ArcNumber.new(30), currency, whitelistContract.address, arc.signer)

      const tx: ContractTransaction = await whitelistContract.claimAllocation(inputAmount)
      await tx.wait()
    } catch (err) {
      console.error(`Error claiming allocation: ${err}`)
    } finally {
      setIsLoading(false)
    }
  }

  let maxAmount = participant.allocation.sub(participant.spent)
  if (usdcBalance.lt(maxAmount)) {
    maxAmount = usdcBalance
  }

  if (hardcap && totalRaised && maxAmount.add(totalRaised).gt(hardcap)) {
    maxAmount = hardcap.sub(totalRaised)
  }

  let hardcapReached = false
  if (hardcap && totalRaised) {
    hardcapReached = totalRaised.gte(hardcap)
  }

  const isInputDisabled =
    participant.allocation.sub(participant.spent).lte(BigNumber.from(0)) ||
    isLoading ||
    inputAmount.gt(maxAmount) ||
    (hardcap && totalRaised.gte(hardcap)) ||
    (hardcap && totalRaised && inputAmount.add(totalRaised).gt(hardcap))

  let buttonContent
  if (isLoading) {
    buttonContent = <CustomLoader />
  } else if (hardcapReached) {
    buttonContent = 'Hardcap reached'
  } else if (!saleStarted) {
    buttonContent = 'The sale has not started'
  } else {
    buttonContent = 'Purchase'
  }
  const errorMessage =
    locationError ||
    (['US'].includes(location?.countryCode) &&
      `Claiming tokens for ${location.country} is not supported`)
  return (
    <div className="phase1-sale">
      <Title
        title={'Phase ' + props.phase + ' Sale'}
        description={
          <>
            <span>
              Thank you for being one of our earliest supporters. You are eligible for the exclusive
              Phase
              {` ${props.phase}`} Sale!{' '}
              <a
                href="https://wiki.arcx.money/token-sale/phase-2-sale"
                target="_blank"
                rel="noopener noreferrer"
              >
                Refer to the wiki
              </a>{' '}
              for full details of the ARCx Token Sale
            </span>
          </>
        }
      />
      <div className="phase1-sale__info-container">
        <InfoBox
          infos={[
            {
              property: 'Your Total Allocation',
              tooltip: 'Maximum amount how much you can purchase',
              value: `$${getReadableNumber(participant.allocation, 2, 6)}`,
            },
            {
              property: 'Purchased Amount',
              tooltip: 'How much you purchased so far',
              value: `$${getReadableNumber(participant.spent, 2, 6)}`,
            },
            {
              property: 'ARCx Price',
              tooltip: 'Your exclusive price for one ARCx token',
              value: props.arcPrice,
            },
            {
              property: 'Terms',
              value: props.terms,
            },
          ]}
        />
        <Input
          assets={[{ value: currency, label: erc20Details?.symbol }]}
          label="Purchase Amount"
          balance={usdcBalance}
          selectedAsset={currency}
          setAmount={setInputAmount}
          maxAmount={maxAmount}
          decimals={6}
        />
        <label className="phase1-sale__checkbox-container" id="phase-1-sale__checkbox-container">
          <input
            type="checkbox"
            checked={isTermsAccepted}
            onChange={() => setIsTermsAccepted((prev) => !prev)}
          />
          <div className="phase1-sale__checkbox-description">
            I have read{' '}
            <a className="phase1-sale__link" href="/token-sale-details.pdf" target="_blank">
              the terms and conditions
            </a>{' '}
            and agree
          </div>
        </label>
        {errorMessage && <p className="phase1-sale__error">{errorMessage}</p>}
        <ArcButton
          onClick={onPurchaseClicked}
          disabled={isInputDisabled || !saleStarted || !isTermsAccepted}
          loading={locationLoading || isLoading}
          name={buttonContent}
          type="primary"
          className="phase1-sale__purchase-btn"
        />
      </div>
    </div>
  )
}

const CustomLoader = () => <Loader type="Grid" color="white" height={25} />
